import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import { saveEvent, listCategories } from '../../common/api';
import { toLocalInput } from '../../common/dates';
const t = (k, p) => app.translator.trans('ernestdefoe-calendar.forum.' + k, p);
/** Create / edit an event. Pass `attrs.event` to edit; `attrs.onsave(ev)` fires on success. */
export default class EventFormModal extends Modal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "data", void 0);
    _defineProperty(this, "categories", []);
    _defineProperty(this, "uploading", false);
  }
  oninit(vnode) {
    var _ev$category;
    super.oninit(vnode);
    const ev = this.attrs.event;
    // Default start: a clicked day/slot if provided, else the next whole hour.
    /*
     * 🚨 A bare 'YYYY-MM-DD' string is parsed as UTC midnight by the spec, which
     * is the PREVIOUS day for anybody west of UTC. The month grid passes a real
     * Date so this is safe today, but a caller passing a date string would seed
     * the form a day out with nothing to show for it.
     */
    const now = this.attrs.day ? asLocalDate(this.attrs.day) : new Date();
    if (!this.attrs.day) {
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
    }
    const later = new Date(now.getTime() + 60 * 60 * 1000);
    this.data = ev ? {
      title: ev.title,
      description: ev.description || '',
      start: toLocalInput(new Date(ev.start)),
      end: ev.end ? toLocalInput(new Date(ev.end)) : '',
      allDay: ev.allDay,
      timezone: ev.timezone || guessTz(),
      location: ev.location || '',
      url: ev.url || '',
      coverUrl: ev.coverUrl || '',
      categoryId: ((_ev$category = ev.category) == null ? void 0 : _ev$category.id) || '',
      repeat: freqOf(ev.rrule)
    } : {
      title: '',
      description: '',
      start: toLocalInput(now),
      end: toLocalInput(later),
      allDay: false,
      timezone: guessTz(),
      location: '',
      url: '',
      coverUrl: '',
      categoryId: '',
      repeat: ''
    };
    listCategories().then(c => {
      this.categories = c;
      m.redraw();
    }).catch(() => {});
  }
  className() {
    return 'CalendarEventModal Modal--medium';
  }
  title() {
    return this.attrs.event ? t('edit_event') : t('add_event');
  }
  content() {
    const d = this.data;
    const field = (label, input) => m('.CalendarForm-field', [m('label.CalendarForm-label', label), input]);

    // Flarum 2's Modal does NOT wrap content() in a form (unlike 1.x), so we must
    // render our own <form> — otherwise the type="submit" button submits nothing
    // and "create" appears to do nothing.
    return m('.Modal-body.CalendarForm', m('form.CalendarForm-form', {
      onsubmit: e => this.onsubmit(e)
    }, [m('input.FormControl.CalendarForm-titleInput', {
      value: d.title,
      placeholder: t('field_title'),
      required: true,
      autofocus: true,
      oninput: e => d.title = e.target.value
    }),
    // ---- When ----
    m('.CalendarForm-section', [m('.CalendarForm-sectionTitle', [m('i.icon.fas.fa-clock'), ' ', t('section_when')]), m('label.CalendarForm-toggle', [m('input', {
      type: 'checkbox',
      checked: d.allDay,
      // 🚨 Switching All-day on must clear the time from the STORED value,
      // not just from what the inputs display. It used to set the flag
      // alone: the fields showed a bare date while d.start still held
      // '…T12:00' and d.end '…T10:00', so an end that looked equal to the
      // start was still an hour earlier underneath. Reported by ClaudiusH
      // as "on the UI the timestamp is removed, but internally still kept".
      onchange: e => {
        d.allDay = e.target.checked;
        if (d.allDay) {
          d.start = d.start.slice(0, 10) + 'T00:00';
          d.end = d.end ? d.end.slice(0, 10) + 'T00:00' : '';
        }
      }
    }), m('span.CalendarForm-toggleTrack'), m('span.CalendarForm-toggleLabel', t('field_all_day'))]), m('.CalendarForm-grid', [field(t('field_start'), m('input.FormControl', {
      type: d.allDay ? 'date' : 'datetime-local',
      value: d.allDay ? d.start.slice(0, 10) : d.start,
      oninput: e => d.start = d.allDay ? e.target.value + 'T00:00' : e.target.value
    })), field(t('field_end'), m('input.FormControl', {
      type: d.allDay ? 'date' : 'datetime-local',
      value: d.allDay ? (d.end || '').slice(0, 10) : d.end,
      oninput: e => d.end = d.allDay ? e.target.value ? e.target.value + 'T00:00' : '' : e.target.value
    }))]), field(t('field_repeat'), m('select.FormControl', {
      value: d.repeat,
      onchange: e => d.repeat = e.target.value
    }, [m('option', {
      value: ''
    }, t('repeat_none')), m('option', {
      value: 'DAILY'
    }, t('repeat_daily')), m('option', {
      value: 'WEEKLY'
    }, t('repeat_weekly')), m('option', {
      value: 'MONTHLY'
    }, t('repeat_monthly')), m('option', {
      value: 'YEARLY'
    }, t('repeat_yearly'))]))]),
    // ---- Details ----
    m('.CalendarForm-section', [m('.CalendarForm-sectionTitle', [m('i.icon.fas.fa-circle-info'), ' ', t('section_details')]), field(t('field_location'), m('input.FormControl', {
      value: d.location,
      placeholder: '123 Main St, City',
      oninput: e => d.location = e.target.value
    })), field(t('field_category'), m('select.FormControl', {
      value: d.categoryId,
      onchange: e => d.categoryId = e.target.value
    }, [m('option', {
      value: ''
    }, '—'), ...this.categories.map(c => m('option', {
      value: c.id
    }, c.name))])), field(t('field_url'), m('input.FormControl', {
      value: d.url,
      placeholder: 'https://…',
      oninput: e => d.url = e.target.value
    })), this.coverField(), field(t('field_description'), m('textarea.FormControl', {
      rows: 4,
      value: d.description,
      placeholder: t('field_description'),
      oninput: e => d.description = e.target.value
    }))]),
    // ---- Actions ----
    m('.CalendarForm-actions', [Button.component({
      className: 'Button',
      type: 'button',
      onclick: () => app.modal.close()
    }, t('cancel')), Button.component({
      type: 'submit',
      className: 'Button Button--primary',
      icon: 'fas fa-check',
      loading: this.loading
    }, t('save'))])]));
  }

  /**
   * Cover image control. With FoF Upload enabled (and the actor permitted), this
   * shows a real file picker; either way a plain URL field remains so covers work
   * without any extra extension installed.
   */
  coverField() {
    const d = this.data;
    const canUpload = !!app.forum.attribute('calendarCoverUploads');
    return m('.Form-group', [m('label', t('field_cover')), d.coverUrl ? m('.CalendarForm-cover', {
      style: {
        backgroundImage: "url(\"".concat(String(d.coverUrl).replace(/"/g, '%22'), "\")")
      }
    }, [m('button.Button.Button--icon.CalendarForm-coverRemove', {
      type: 'button',
      title: t('cover_remove'),
      onclick: () => d.coverUrl = ''
    }, m('i.fas.fa-times'))]) : null, m('.CalendarForm-coverControls', [canUpload ? m('label.Button.CalendarForm-upload' + (this.uploading ? '.disabled' : ''), [m('i.Button-icon.fas.fa-upload'), m('span.Button-label', this.uploading ? t('cover_uploading') : t('cover_upload')), m('input', {
      type: 'file',
      accept: 'image/*',
      disabled: this.uploading,
      onchange: e => this.uploadCover(e)
    })]) : null, m('input.FormControl', {
      value: d.coverUrl,
      placeholder: canUpload ? t('cover_or_url') : 'https://…/cover.jpg',
      oninput: e => d.coverUrl = e.target.value
    })])]);
  }
  uploadCover(e) {
    var _e$target;
    const file = (_e$target = e.target) == null || (_e$target = _e$target.files) == null ? void 0 : _e$target[0];
    if (!file) return;
    this.uploading = true;
    m.redraw();
    const body = new FormData();
    body.append('files[]', file);

    // Use Flarum's app.request rather than a raw fetch: it attaches the CSRF
    // token and sends cookies for us, and Mithril passes a FormData body through
    // untouched (no JSON encoding, browser sets the multipart boundary). We
    // suppress the generic request-error alert (errorHandler) and surface our own
    // cover-specific one instead; the two-arg then() keeps the cleanup running on
    // both success and failure.
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/fof/upload',
      body,
      errorHandler: () => {}
    }).then(res => {
      var _res$data;
      const f = (res == null || (_res$data = res.data) == null || (_res$data = _res$data[0]) == null ? void 0 : _res$data.attributes) || {};
      const url = f.url || f.relativeUrl || f.uploadUrl;
      if (url) this.data.coverUrl = url;else app.alerts.show({
        type: 'error'
      }, t('cover_error'));
    }, () => app.alerts.show({
      type: 'error'
    }, t('cover_error'))).then(() => {
      this.uploading = false;
      m.redraw();
    });
  }
  onsubmit(e) {
    var _this$attrs$event;
    e.preventDefault();
    this.loading = true;
    const d = this.data;
    const attrs = {
      title: d.title,
      description: d.description,
      start: localToIso(d.start, d.allDay),
      end: d.end ? localToIso(d.end, d.allDay) : null,
      allDay: d.allDay,
      timezone: d.timezone,
      location: d.location,
      url: d.url,
      coverUrl: d.coverUrl,
      categoryId: d.categoryId || null,
      rrule: d.repeat ? 'FREQ=' + d.repeat : null
    };
    saveEvent(attrs, (_this$attrs$event = this.attrs.event) == null ? void 0 : _this$attrs$event.id).then(ev => {
      var _this$attrs$onsave, _this$attrs;
      (_this$attrs$onsave = (_this$attrs = this.attrs).onsave) == null || _this$attrs$onsave.call(_this$attrs, ev);
      app.modal.close();
      app.alerts.show({
        type: 'success'
      }, t('saved'));
    }).catch(() => {
      this.loading = false;
      app.alerts.show({
        type: 'error'
      }, t('save_error'));
      m.redraw();
    });
  }
}
function guessTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
}
function freqOf(rrule) {
  const m2 = /FREQ=([A-Z]+)/.exec(rrule || '');
  return m2 ? m2[1] : '';
}
/** A Date is used as-is; a 'YYYY-MM-DD' string is read as a LOCAL date. */
function asLocalDate(value) {
  if (value instanceof Date) return new Date(value.getTime());
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(value);
}

/**
 * A datetime-local string is local wall time → ISO (UTC).
 *
 * 🚨 An all-day event is a DATE, not an instant, and must never be run through
 * the reader's offset. It used to be: the oninput handlers append 'T00:00', and
 * `new Date('2026-12-29T00:00')` is parsed as LOCAL midnight while
 * `new Date('2026-12-29')` is parsed as UTC midnight — a difference the
 * ECMAScript spec requires. So a start that had been seeded from a clicked day
 * and an end the user had retyped were converted by two different rules, and
 * east of UTC the end landed an hour BEFORE the start. The server then refused
 * it with "The end must be after the start" on two dates that were the same
 * day, which is exactly what it looked like: nonsense.
 *
 * Pinning the date part to UTC midnight makes both ends of an all-day event
 * the same kind of value, and stops an all-day event drifting a day for
 * readers in another timezone.
 */
function localToIso(val, allDay) {
  if (allDay) {
    return new Date(val.slice(0, 10) + 'T00:00:00Z').toISOString();
  }
  return new Date(val).toISOString();
}
flarum.reg.add('ernestdefoe-calendar', 'forum/components/EventFormModal', EventFormModal);